import React from 'react';
import {
  Alert,
  Box,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useValidationStore } from '../../store/validationStore';
import { AttemptCard } from './AttemptCard';
import { ValidationStatusBadge } from './ValidationStatusBadge';

export const DRAWER_WIDTH = 400;

interface Props {
  /** Toggle button anchor — rendered when the drawer is closed. */
  renderToggle?: (open: () => void) => React.ReactNode;
}

export const ValidationDrawer: React.FC<Props> = ({ renderToggle }) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const {
    drawerOpen,
    openDrawer,
    closeDrawer,
    status,
    currentAttempt,
    maxAttempts,
    attempts,
    finalDecision,
    finalScore,
    qualityWarning,
  } = useValidationStore();

  const isRunning = status === 'running';
  const variant = isMdUp ? 'persistent' : 'temporary';

  const statusLabel = () => {
    if (status === 'idle') return 'Idle';
    if (isRunning) return `Validating — Attempt ${currentAttempt} of ${maxAttempts}`;
    if (status === 'complete') return 'Complete';
    if (status === 'error') return 'Error';
    return '';
  };

  const tabColor =
    status === 'running' ? 'error.main'
    : status === 'complete' ? 'success.main'
    : 'primary.main';

  return (
    <>
      {/* Toggle button rendered outside the drawer (caller controls placement) */}
      {!drawerOpen && renderToggle && renderToggle(openDrawer)}

      {/* ── Edge tab — always visible when panel is closed ── */}
      {!drawerOpen && (
        <Box
          onClick={openDrawer}
          sx={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: (t) => t.zIndex.drawer - 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            px: 0.75,
            py: 1.5,
            bgcolor: tabColor,
            color: '#fff',
            borderRadius: '6px 0 0 6px',
            cursor: 'pointer',
            boxShadow: 3,
            transition: 'opacity 0.2s',
            '&:hover': { opacity: 0.85 },
          }}
        >
          <VerifiedIcon sx={{ fontSize: 18 }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)',
              fontSize: 10,
              letterSpacing: 0.5,
              lineHeight: 1,
            }}
          >
            Quality
          </Typography>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        variant={variant}
        onClose={closeDrawer}
        sx={{
          width: drawerOpen && isMdUp ? DRAWER_WIDTH : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            // Sit below the AppBar only when persistent (desktop)
            ...(isMdUp && {
              top: 64,
              height: 'calc(100% - 64px)',
            }),
          },
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <VerifiedIcon color="primary" sx={{ fontSize: 20 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              AI Quality Validation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isRunning && (
                <RadioButtonCheckedIcon
                  sx={{ fontSize: 10, color: 'error.main', animation: 'pulse 1s infinite' }}
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {statusLabel()}
              </Typography>
            </Box>
          </Box>

          {finalDecision && (
            <ValidationStatusBadge decision={finalDecision} score={finalScore} />
          )}

          <Tooltip title="Close panel">
            <IconButton size="small" onClick={closeDrawer}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Quality warning banner ── */}
        {qualityWarning && (
          <Alert
            severity="warning"
            sx={{ mx: 2, mt: 1.5, flexShrink: 0, fontSize: 12 }}
          >
            {qualityWarning}
          </Alert>
        )}

        {/* ── Attempt list ── */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {status === 'idle' && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.disabled',
                gap: 1,
              }}
            >
              <VerifiedIcon sx={{ fontSize: 48 }} />
              <Typography variant="body2">
                Quality validation results will appear here when you generate content.
              </Typography>
            </Box>
          )}

          {attempts.map((attempt, idx) => (
            <AttemptCard
              key={attempt.attemptNumber}
              attempt={attempt}
              previousAttempt={idx > 0 ? attempts[idx - 1] : null}
              isActive={attempt.attemptNumber === currentAttempt && isRunning}
            />
          ))}
        </Box>
      </Drawer>

      {/* Pulse animation keyframes */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </>
  );
};
